/**
 * Repeatable custom blocks on the quest page (tip, quote, link, divider, quiz, gallery).
 */
import { MAX_GALLERY_ITEMS } from "./gallery-photo.ts";

export const PAGE_BLOCK_KINDS = ["tip", "quote", "link", "divider", "quiz", "gallery"] as const;
export type PageBlockKind = (typeof PAGE_BLOCK_KINDS)[number];

export const BLOCK_SECTION_PREFIX = "block:";

export const MAX_PAGE_BLOCKS = 48;
export const MAX_TIP_TEXT = 500;
export const MAX_QUOTE_TEXT = 500;
export const MAX_QUOTE_ATTRIBUTION = 120;
export const MAX_LINK_URL = 2048;
export const MAX_LINK_TITLE = 200;
export const MAX_QUIZ_TEXT = 500;
export const MAX_QUIZ_HINT = 300;

export interface GalleryItemState {
  image?: string | null;
  caption?: string | null;
}

export interface TipBlockData {
  kind: "tip";
  text: string;
}

export interface QuoteBlockData {
  kind: "quote";
  text: string;
  attribution?: string | null;
}

export interface LinkBlockData {
  kind: "link";
  url: string;
  title?: string | null;
  image?: string | null;
}

export interface DividerBlockData {
  kind: "divider";
}

export interface QuizBlockData {
  kind: "quiz";
  question: string;
  answer: string;
  hint?: string | null;
}

export interface GalleryBlockData {
  kind: "gallery";
  items: GalleryItemState[];
}

export type QuestPageBlock =
  | TipBlockData
  | QuoteBlockData
  | LinkBlockData
  | DividerBlockData
  | QuizBlockData
  | GalleryBlockData;

export function blockSectionId(index: number): string {
  return `${BLOCK_SECTION_PREFIX}${index}`;
}

export function parseBlockSectionId(id: string): number | null {
  if (!id.startsWith(BLOCK_SECTION_PREFIX)) return null;
  const n = Number(id.slice(BLOCK_SECTION_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export function isPageBlockKind(value: string): value is PageBlockKind {
  return (PAGE_BLOCK_KINDS as readonly string[]).includes(value);
}

export function createDefaultPageBlock(kind: PageBlockKind): QuestPageBlock {
  switch (kind) {
    case "tip":
      return { kind: "tip", text: "" };
    case "quote":
      return { kind: "quote", text: "", attribution: null };
    case "link":
      return { kind: "link", url: "", title: null, image: null };
    case "divider":
      return { kind: "divider" };
    case "quiz":
      return { kind: "quiz", question: "", answer: "", hint: null };
    case "gallery":
      return { kind: "gallery", items: [{ image: null, caption: null }] };
  }
}

export function alignPageBlocksWithListOrder<T extends QuestPageBlock>(
  order: string[],
  blocks: T[],
): { order: string[]; blocks: T[] } {
  const perm = order.map((id) => parseBlockSectionId(id)).filter((n): n is number => n != null);
  if (perm.length !== blocks.length) return { order, blocks };
  const nextBlocks = perm.map((i) => blocks[i]!);
  let block = 0;
  const nextOrder = order.map((id) => (parseBlockSectionId(id) != null ? blockSectionId(block++) : id));
  return { order: nextOrder, blocks: nextBlocks };
}

/** Palette-only ids for adding a new block (same string as block kind). */
export const PALETTE_PAGE_BLOCK_KINDS = PAGE_BLOCK_KINDS;

export function defaultGalleryItemCount(block: GalleryBlockData): number {
  return Math.min(Math.max(block.items.length, 1), MAX_GALLERY_ITEMS);
}
